import React, { useReducer, useState, useEffect } from 'react';
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import SectionTitleBar from '../components/titlebar';

import './Facturar.scss';
import { Bill, ToBillArguments, BillConceptos, ToBillEvent } from '../../models/Factura';
import { useQuery } from '@apollo/react-hooks';
import { graphqlSchema } from '../../services/graphql.schema';
import FacturarFormato from './components/Facturar.formato';
import FacturarConceptos from './conceptos/Factura.conceptos';
import FacturaTotales from './components/Factura.totales';
import FacturaReceptor, { ComprobanteProperty, ReceptorProperty } from './components/Factura.receptor';
import FacturaGlobal from './components/Factura.global';
import FacturaActions from './components/Factura.actions';
import { useHistory, useParams } from 'react-router-dom';
import { validateCfdiIsComplete } from '../../services/validation.cfdi';
import { Receptor, SerieTracker } from '../../models/Catalogos';
import { validateBillArgumentos } from '../../services/validation.arguments';
import { structureBillArgumentsToBillEvents } from './handlers/billArgumentsToBillEvents';
import { applyGlobalBillDefaults, billReducer, cfdiCleaner, EMPTY_BILL, EMPTY_BILL_ARGUMENTS } from './handlers/factura.state';
import { FechaOptions, GENERIC_RFC, setFechaOptions } from './constants/factura.options';
import { useReceptorSearch } from '../Hooks/useReceptorSearch';

interface DashboardProps extends BaseReact {
    currentUser: ProfileQL;
}

function Facturar({ currentUser, location }: DashboardProps) {
    const history = useHistory();
    const { argumentid, eventid } = useParams<{ argumentid?: string, eventid?: string }>();

    const [tracekrOptions, setTrackerOptions] = useState<SerieTracker[]>([]);
    const { receptorOptions, searchReceptorByName, searchReceptorByRfc } = useReceptorSearch({ initialSearch: ' ' });

    const [validRequested, setValidRequested] = useState(false);
    const [validConceptos, setValidConceptos] = useState(false);
    const [invalidArguments, setInvalidArguments] = useState<{ key: string; text: string }>(null);
    const [usoCfdiError, setUsoCfdiError] = useState('');

    // Change from To Publico en General to Global Bill Override
    const [asGlobal, setAsGlobal] = useState(false);
    const [fechaOptions] = useState<FechaOptions>(setFechaOptions(Date.now()));

    const [cfdi, dispatchBill] = useReducer(billReducer, Object.assign({ fecha: '' }, EMPTY_BILL()));
    const [billArgumento, _setBillArgumento] = useState<ToBillArguments<ToBillEvent>>(EMPTY_BILL_ARGUMENTS())

    const [usosCFDI, setUsosCFDI] = useState<{ value: string, label: string, forRegimen: string[] }[]>([]);
    const [formasPago, setFormasPago] = useState<{ value: string, label: string }[]>([]);

    useEffect(() => {
        const state = location.state as { billArgument: ToBillArguments<ToBillEvent>; billEvents: ToBillEvent[]; paymentEvents: ToBillEvent[] };
        console.log('FACTURA Recover State: ', state);
        if (state && state.billArgument) {
            if (!!state.billEvents && state.billArgument.multipayType === 1) {
                // BillArgument CFDI is original MasterCFDI
                dispatchBill({ property: '_replace', value: state.billEvents[0].CFDI })
            } else {
                // Some cases, lugar de Expedición might get lost when re-editing bills
                dispatchBill({ property: '_replace', value: cfdiCleaner(state.billArgument.MasterCFDI, currentUser.fiscalpopProfile, fechaOptions) })
            }
            setBillArgumento({
                timeDefered: state.billArgument.timeDefered,
                multipayType: state.billArgument.multipayType,
                multiPayments: state.billArgument.multiPayments,
                upfront: state.billArgument.upfront,
                frequency: state.billArgument.frequency,
                isClosed: state.billArgument.isClosed,
                createdAt: state.billArgument.createdAt,
                billEvents: state.billArgument.billEvents,
                paymentEvents: state.billArgument.paymentEvents,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    // GRAPHQL ---------
    useQuery(graphqlSchema.PROFILE.CATALOGOS.getAllTrackers, {
        onCompleted: ({ getAllTrackers }: { getAllTrackers: SerieTracker[] }) => {
            setTrackerOptions(getAllTrackers.filter(t => !t.receptorRfc));
            const trackerUsing = getAllTrackers.find(t => t.serie === cfdi.serie);
            if (trackerUsing && trackerUsing.folio) {
                dispatchBill({ property: 'comprobante', value: { folio: `${trackerUsing.folio}` } })
            }
        },
        onError: (e) => {
            console.error('Error getting by name: ', e.graphQLErrors[0])
        },
        fetchPolicy: 'cache-and-network'
    })

    useQuery(graphqlSchema.FISCALPOP.USO_CFDI.getUsoCfdi, {
        onCompleted: ({ getUsoCfdi }: { getUsoCfdi: { clave: string, descripcion: string, forRegimen: string[] }[] }) => {
            setUsosCFDI(getUsoCfdi.map(ucfdi => ({ value: ucfdi.clave, label: `(${ucfdi.clave}) ${ucfdi.descripcion}`, forRegimen: ucfdi.forRegimen })).sort((a, b) => a.value.localeCompare(b.value)));
        },
        onError: (e) => {
            console.error('Error getting uso CFDI: ', e);
        }
    })
    useQuery(graphqlSchema.FISCALPOP.FORMA_PAGO.getFormaPago, {
        onCompleted: ({ getFormaPago }: { getFormaPago: { key: string, label: string }[] }) => {
            setFormasPago(getFormaPago.map(gFP => ({ value: gFP.key, label: `(${gFP.key}) ${gFP.label}` })));
        },
        onError: (e) => {
            console.error('Error getting uso CFDI: ', e);
        }
    })

    useEffect(() => {
        // Execute only when validation failed before
        if (validRequested) {
            if (validateCfdiIsComplete(cfdi, validConceptos)) {
                setValidRequested(false);
            }
        }
    }, [cfdi, validRequested, validConceptos])

    // Publico General to Global bill check and modify (UE)
    useEffect(() => {
        if (!cfdi.receptor?.rfc) {
            return;
        }
        if (cfdi.receptor.rfc === GENERIC_RFC && asGlobal) {
            if (!cfdi.global) {
                console.log(`UE RFC Generic Enabled: `, cfdi);
                // if global is present, do not execute again
                dispatchBill({ property: 'comprobante', value: applyGlobalBillDefaults(cfdi, currentUser) })

                // Change Bill argument to be PUE
                _setBillArgumento(Object.assign({}, billArgumento, { timeDefered: 0, multipayType: 0, upfront: 0 }));
            }
        } else {
            // Exiting Generic RFC
            if (cfdi.global) {
                console.log(`UE RFC Generic Disabled: `, cfdi);
                dispatchBill({ property: 'global', value: null })
            }
            // Also reset asGlobal to false, users might try to "Reset" changing RFC without noticing the switch effect
            if (asGlobal) {
                setAsGlobal(false);
            }
        }
    }, [cfdi, billArgumento, currentUser, asGlobal])

    // Handlers ============

    const handleReceptorChange = (property: ReceptorProperty) => (value: string) => {
        const receptor = Object.assign({}, cfdi.receptor);
        if (property === 'rfc') {
            value = (value || '').toUpperCase();
        }
        if (property === 'email') {
            value = (value || '').toLowerCase().trim();
        }
        receptor[property] = value;
        dispatchBill({ property: 'receptor', value: receptor });
        if (property === 'nombre') {
            searchReceptorByName(value);
        }
        if (property === 'rfc') {
            searchReceptorByRfc(value);
        }
    }

    const handleReceptorAutofill = (receptorAsString: string) => {
        const receptor: Receptor = JSON.parse(receptorAsString);
        console.log('Receptor to add: ', receptor);
        const _receptor = Object.assign({}, cfdi.receptor, { nombre: receptor.name, rfc: receptor.rfc, customerId: receptor._id });
        if (receptor.email) {
            _receptor.email = receptor.email;
        }
        if (receptor.zip) {
            _receptor.zip = receptor.zip;
        }
        if (receptor.regimen) {
            _receptor.regimen = receptor.regimen;
        }
        const comprobante: Bill = Object.assign({}, cfdi);
        comprobante.receptor = _receptor;
        if (receptor.seriePreference) {
            // Pull serie ref
            comprobante.serie = receptor.seriePreference;
            // get Tracker reference for folio
            const hasFolio = tracekrOptions.find(tO => tO.serie === receptor.seriePreference);
            if (!!hasFolio) {
                comprobante.folio = `${hasFolio.folio}`;
            }
        }
        // Change to Generic RFC should also update some comprobante fields, UE is ONLY for additional Global changes
        if (receptor.rfc === GENERIC_RFC) {
            comprobante.receptor.usoCFDI = 'S01';   // Specific uso de CFDI for Publico en General
            comprobante.receptor.email = currentUser.professionalProfile.email;
            comprobante.receptor.zip = currentUser.fiscalpopProfile.lugarExpedicion;
        }
        dispatchBill({ property: 'comprobante', value: comprobante });
    }

    const handleGlobalChange = (property: 'periodo' | 'mes' | 'ano') => (value: any) => {
        const global = Object.assign({}, cfdi.global);
        if (property === 'ano' && value.length === 4) {
            const aV = parseInt(value);
            if (aV < 2022) {
                value = '2022'
            } else if (aV > new Date().getFullYear()) {
                value = `${new Date().getFullYear()}`
            }
        }
        global[property] = value;
        dispatchBill({ property: 'global', value: global })
    }

    const handleComprobanteChange = (property: ComprobanteProperty) => (value: any) => {
        const comprobante = Object.assign(cfdi);
        comprobante[property] = value;
        if (property === 'serie') {
            const tOption = tracekrOptions.find(t => t.serie === value);
            if (!!tOption) {
                comprobante['folio'] = `${tOption.folio}`;
            }
        }
        dispatchBill({ property: 'comprobante', value: comprobante });
    }

    const setBillArgumento = (argument: ToBillArguments<ToBillEvent>) => {
        _setBillArgumento(argument);
        setInvalidArguments(null);
    }

    const handleConceptosChange = (updated: BillConceptos[]) => {
        dispatchBill({ property: 'conceptos', value: updated });
    }

    const _clearBill = () => {
        dispatchBill({ property: '_replace', value: EMPTY_BILL() });
        setBillArgumento(EMPTY_BILL_ARGUMENTS())
        setAsGlobal(false);
        if (argumentid) {
            history.push({
                state: {},
                pathname: '/facturacion/facturar'
            })
        }
    }

    const validateRegimenUsoCfdi = () => {
        const canUseByRegimen = !usosCFDI.length ? true : usosCFDI.find(u => u.value === cfdi.receptor.usoCFDI).forRegimen.includes(cfdi.receptor.regimen)
        const invalidUsoCfdi = !!cfdi.receptor.usoCFDI && !!cfdi.receptor.regimen && !canUseByRegimen ? 'Uso de CFDI no aplica para el régimen de receptor' : ''
        return invalidUsoCfdi;
    }

    const submitPreview = () => {
        console.log('PREVIEW BILL (billArgumento): ', billArgumento)
        console.log('PREVIEW BILL (cfdi): ', cfdi);

        // Confirm that CFDI is correct else, state error
        if (!validateCfdiIsComplete(cfdi, validConceptos)) {
            setValidRequested(true);
            return;
        }
        // Confirm that upfront will not EXCEED bill's total, etc.
        const _invalidArguments = validateBillArgumentos(billArgumento, cfdi);
        if (_invalidArguments) {
            setInvalidArguments(_invalidArguments);
            setValidRequested(false);
            return;
        }

        const _usoCfdiError = validateRegimenUsoCfdi();
        if (_usoCfdiError) {
            setUsoCfdiError(_usoCfdiError);
            setValidRequested(false);
            return;
        }

        const { billArgument, billEvents, paymentEvents } = structureBillArgumentsToBillEvents(currentUser, billArgumento, cfdi);
        console.log('PREVIEW BILL (templates): ', { billArgument, billEvents, paymentEvents });
        const pathName = eventid ? `/facturacion/facturar/preview/${argumentid}/${eventid}` : (argumentid ? `/facturacion/facturar/preview/${argumentid}` : '/facturacion/facturar/preview');
        history.push({
            pathname: pathName,
            state: { billArgument, billEvents, paymentEvents }
        })
    }

    return (
        <div id="Facturar">
            <SectionTitleBar currentUser={currentUser} title={argumentid ? `Editar Factura` : `Emitir Factura`} />
            <div className="facturarContent">
                <FacturaReceptor
                    cfdi={cfdi}
                    validRequested={validRequested}
                    invalidUsoCfdi={validateRegimenUsoCfdi()}
                    asGlobal={asGlobal}
                    fechaOptions={fechaOptions}
                    receptorOptions={receptorOptions}
                    trackerOptions={tracekrOptions}
                    usosCFDI={usosCFDI}
                    onAsGlobalChange={setAsGlobal}
                    onReceptorChange={handleReceptorChange}
                    onReceptorAutofill={handleReceptorAutofill}
                    onComprobanteChange={handleComprobanteChange}
                />
                <FacturaGlobal global={cfdi.global} onGlobalChange={handleGlobalChange} />
                <div className="global">
                    <FacturarFormato cfdi={cfdi} isGlobalBill={!!cfdi.global} onCfdiChange={handleComprobanteChange} billArguments={billArgumento} invalidArguments={invalidArguments} onBillArgumentsChange={setBillArgumento} formaDePagoOptions={formasPago} />
                </div>
                <FacturarConceptos
                    conceptos={cfdi.conceptos}
                    isGlobalBill={!!cfdi.global}
                    validRequested={validRequested}
                    authToken={currentUser.professionalProfile.authToken}
                    onCfdiChange={handleConceptosChange}
                    onValidationTest={setValidConceptos}
                />
                <FacturaTotales cfdi={cfdi} />
                <FacturaActions
                    validRequested={validRequested}
                    validConceptos={validConceptos}
                    invalidArguments={invalidArguments}
                    usoCfdiError={usoCfdiError}
                    onClear={_clearBill}
                    onPreview={submitPreview}
                />
            </div>
        </div>
    )
}

export default Facturar;
